/**
 * @deprecated Use `ch-tabular-grid` component instead. Use `TabularGridCellType` instead.
 *
 * ChGridCellType indicates the type of cell.
 * "Plain" is a simple cell that shows the contents of it.
 * "Rich" is a rich cell that, in addition to displaying its content, enables
 * the user to execute different actions on the row.
 * The actions are:
 * - allow dragging the row to reorder it.
 * - allow to select the row by means of a checkbox.
 * - allow displaying actions to be executed in the row.
 * "TreeNode" is a cell that represents a node of the Tree.
 */
export var ChGridCellType;
(function (ChGridCellType) {
    ChGridCellType["Plain"] = "plain";
    ChGridCellType["Rich"] = "rich";
    ChGridCellType["TreeNode"] = "node";
})(ChGridCellType || (ChGridCellType = {}));
/**
 * The `ch-grid-cell` component represents a grid cell.
 * @deprecated Use `ch-tabular-grid` component instead. Use `ch-tabular-grid-cell` instead.
 */
export default class HTMLChGridCellElement extends HTMLElement {
    cellType = ChGridCellType.Plain;
    caret;
    drag;
    action;
    selector;
    selectorLabel;
    rowDrag;
    rowSelector;
    rowActions;
    static get observedAttributes() {
        return ["cell-type", "row-drag", "row-selector", "row-actions"];
    }
    constructor() {
        super();
        this.defineFocusHandler();
    }
    connectedCallback() {
        if (this.cellType !== ChGridCellType.Plain) {
            this.define();
        }
    }
    attributeChangedCallback(name, _oldValue, value) {
        if (name === "cell-type") {
            this.cellType = value;
        }
        if (name === "row-drag") {
            this.cellType = ChGridCellType.Rich;
            this.rowDrag = value !== null ? value !== "false" : false;
        }
        if (name === "row-selector") {
            this.cellType = ChGridCellType.Rich;
            this.rowSelector = value !== null ? value !== "false" : false;
        }
        if (name === "row-actions") {
            this.cellType = ChGridCellType.Rich;
            this.rowActions = value !== null ? value !== "false" : false;
        }
    }
    /**
     * One of "plain", "rich", or "node", indicating the type of cell.
     */
    get type() {
        return this.cellType;
    }
    set type(value) {
        if (this.cellType !== value) {
            this.cellType = value;
            this.define();
        }
    }
    /**
     * Returns the parent ch-grid element of the cell.
     */
    get grid() {
        return this.closest("ch-grid");
    }
    /**
     * Returns the ch-grid-column element of the cell.
     */
    get column() {
        const cellIndex = Array.prototype.indexOf.call(this.row.querySelectorAll(`:scope > ch-grid-cell`), this);
        return this.grid.querySelector(`ch-grid-column:nth-of-type(${cellIndex + 1})`);
    }
    /**
     * Returns the parent ch-grid-row element of the cell.
     */
    get row() {
        return this.parentElement;
    }
    /**
     * A unique identifier for the cell.
     */
    get cellId() {
        return this.getAttribute("cellid") ?? "";
    }
    /**
     * A boolean value indicating whether the cell is selected.
     */
    get selected() {
        return this.hasAttribute("selected");
    }
    set selected(value) {
        if (value === true) {
            this.setAttribute("selected", "");
        }
        else {
            this.removeAttribute("selected");
        }
    }
    /**
     * A boolean value indicating whether the cell is focused.
     */
    get focused() {
        return this.hasAttribute("focused");
    }
    set focused(value) {
        if (value === true) {
            this.setAttribute("focused", "");
        }
        else {
            this.removeAttribute("focused");
        }
    }
    /**
     * A boolean value indicates whether the grid cell is visible.
     */
    isVisible() {
        return this.offsetParent !== null;
    }
    /**
     * Ensures that the cell is visible within the control, scrolling the contents of the control if necessary.
     */
    ensureVisible() {
        this.dispatchEvent(new CustomEvent("cellEnsureVisible", { bubbles: true, composed: true }));
    }
    /**
     * A boolean value indicates whether the selector of cell is checked or not.
     */
    setSelectorChecked(value) {
        this.selector.checked = value;
        if (this.selector.checked) {
            this.selector.setAttribute("part", "selector checked");
        }
        else {
            this.selector.setAttribute("part", "selector");
        }
    }
    defineFocusHandler() {
        this.addEventListener("focusin", () => {
            if (!this.rowSelector) {
                this.dispatchEvent(new CustomEvent("cellFocused", { bubbles: true, composed: true }));
            }
        });
    }
    caretMouseDownHandler(eventInfo) {
        eventInfo.stopPropagation();
        this.dispatchEvent(new CustomEvent("cellCaretClicked", { bubbles: true, composed: true }));
    }
    selectorClickHandler(eventInfo) {
        eventInfo.stopPropagation();
        this.dispatchEvent(new CustomEvent("cellSelectorClicked", {
            bubbles: true,
            composed: true,
            detail: {
                checked: this.selector.checked,
                range: eventInfo.shiftKey
            }
        }));
    }
    selectorLabelClickHandler(eventInfo) {
        if (!eventInfo.shiftKey) {
            eventInfo.stopPropagation();
        }
    }
    dragMouseDownHandler(eventInfo) {
        eventInfo.preventDefault();
        eventInfo.stopPropagation();
        this.dispatchEvent(new CustomEvent("rowDragStarted", {
            bubbles: true,
            composed: true,
            detail: {
                row: this.row
            }
        }));
    }
    actionClickHandler() {
        this.dispatchEvent(new CustomEvent("cellRowActionClicked", {
            bubbles: true,
            composed: true
        }));
    }
    define() {
        switch (this.cellType) {
            case ChGridCellType.Rich:
                this.defineRich();
                break;
            case ChGridCellType.TreeNode:
                this.defineTreeNode();
                break;
        }
    }
    defineRich() {
        let html = "";
        if (!this.shadowRoot || this.shadowRoot.innerHTML === "") {
            this.attachShadow({ mode: "open" });
            if (this.rowDrag) {
                html += `
          <div part="drag-icon"></div>
        `;
            }
            if (this.rowSelector) {
                html += `
          <style>label {display:flex}</style>
          <label part="selector-label">
            <input type="checkbox" part="selector">
          </label>
        `;
            }
            if (this.rowActions) {
                html += `
          <button part="actions-icon"></button>
        `;
            }
            this.shadowRoot.innerHTML = `
        ${html}
        <slot></slot>
      `;
            if (this.rowDrag) {
                this.drag = this.shadowRoot.querySelector("[part='drag-icon']");
                this.drag.addEventListener("mousedown", this.dragMouseDownHandler.bind(this));
            }
            if (this.rowActions) {
                this.action = this.shadowRoot.querySelector("[part='actions-icon']");
                this.action.addEventListener("click", this.actionClickHandler.bind(this));
            }
            if (this.rowSelector) {
                this.selector = this.shadowRoot.querySelector("[part='selector']");
                this.selector.addEventListener("mousedown", (eventInfo) => eventInfo.stopPropagation());
                this.selector.addEventListener("touchend", (eventInfo) => eventInfo.stopPropagation());
                this.selector.addEventListener("click", this.selectorClickHandler.bind(this));
                this.selectorLabel = this.shadowRoot.querySelector("[part='selector-label']");
                this.selectorLabel.addEventListener("mousedown", (eventInfo) => eventInfo.stopPropagation());
                this.selectorLabel.addEventListener("touchend", (eventInfo) => eventInfo.stopPropagation());
                this.selectorLabel.addEventListener("click", this.selectorLabelClickHandler.bind(this));
            }
        }
    }
    defineTreeNode() {
        if (!this.shadowRoot || this.shadowRoot.innerHTML === "") {
            this.attachShadow({ mode: "open" });
            this.shadowRoot.innerHTML = `
        <style>:host::before {content: none !important}</style>
        <div part="indent"></div>
        <div part="caret"></div>
        <input type="checkbox" part="node-selector" hidden>
        <div part="node-icon"></div>
        <slot></slot>
      `;
            this.caret = this.shadowRoot.querySelector("[part='caret']");
            this.caret.addEventListener("mousedown", this.caretMouseDownHandler.bind(this));
        }
    }
}
if (!customElements.get("ch-grid-cell")) {
    customElements.define("ch-grid-cell", HTMLChGridCellElement);
}
